import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Flex, Text } from 'rebass/styled-components';
import pluralize from 'pluralize';

const TABLE_CONTAINER_SELECTOR = '.ag-theme-alpine';

export const BulkActionsBar = ({ count, itemLabel, actions, sx }) => {
  const barRef = useRef(null);
  const [leftPosition, setLeftPosition] = useState('50%');

  const updatePosition = useCallback(() => {
    requestAnimationFrame(() => {
      const tableContainer = document.querySelector(TABLE_CONTAINER_SELECTOR);
      if (tableContainer && barRef.current) {
        const tableRect = tableContainer.getBoundingClientRect();
        const tableCenter = tableRect.left + tableRect.width / 2;
        setLeftPosition(`${tableCenter}px`);
      } else {
        setLeftPosition('50%');
      }
    });
  }, []);

  // Set up event listeners and observer once
  useEffect(() => {
    updatePosition();

    const timeoutId = setTimeout(updatePosition, 100);

    window.addEventListener('resize', updatePosition);
    // Listen to window scroll without capture phase for better performance
    window.addEventListener('scroll', updatePosition, false);

    // Query table container once and set up observer if available
    const tableContainer = document.querySelector(TABLE_CONTAINER_SELECTOR);
    let observer = null;
    if (tableContainer?.parentElement) {
      observer = new MutationObserver(updatePosition);
      observer.observe(tableContainer.parentElement, {
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, false);
      if (observer) {
        observer.disconnect();
      }
    };
  }, [updatePosition]);

  // Update position when count changes
  useEffect(() => {
    updatePosition();
  }, [count, updatePosition]);

  return (
    <Flex
      ref={barRef}
      sx={{
        mx: 'auto',
        width: 'auto',
        bottom: count > 0 ? '16px' : '-64px',
        position: 'fixed',
        height: '48px',
        borderRadius: '64px',
        alignSelf: 'center',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        color: '#FFFFFF',
        left: leftPosition,
        transform: 'translate(-50%, 0)',
        backgroundColor: 'purple.500',
        maxWidth: 'calc(100vw - 32px)',
        transition: 'all ease .3s',
        zIndex: 2,
        px: '24px',
        ...sx,
      }}
    >
      <Text sx={{ flex: 'none', whiteSpace: 'nowrap' }} fontSize="14px" fontWeight={500}>{`${count} ${pluralize(
        itemLabel,
        count,
      )} selected:`}</Text>
      <Flex sx={{ alignItems: 'center', gap: '8px' }}>{actions}</Flex>
    </Flex>
  );
};

BulkActionsBar.propTypes = {
  count: PropTypes.number.isRequired,
  itemLabel: PropTypes.string,
  actions: PropTypes.node,
  sx: PropTypes.object,
};

BulkActionsBar.defaultProps = {
  itemLabel: 'item',
  actions: null,
  sx: {},
};
